import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CircleMarker, MapContainer, Polyline, TileLayer, Tooltip, useMap, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { leafletTileLayerProps, resolveBasemap } from '../utils/mapBasemap';
import { REVIEW_COLORS, colorMetrics, lassoSelect, quantileRamp } from './selection.js';

function Fit({ bounds }) {
  const map = useMap();
  const done = useRef(false);
  useEffect(() => {
    if (done.current || !bounds) return;
    done.current = true;
    map.fitBounds(bounds, { padding: [30, 30], maxZoom: 12, animate: false });
  }, [map, bounds]);
  return null;
}

/** Freehand lasso: drag with the map's panning disabled while lasso mode is on. */
function Lasso({ active, onDone }) {
  const [pts, setPts] = useState([]);
  const drawing = useRef(false);
  const map = useMapEvents({
    mousedown(e) {
      if (!active) return;
      drawing.current = true;
      setPts([e.latlng]);
    },
    mousemove(e) {
      if (!active || !drawing.current) return;
      setPts((p) => [...p, e.latlng]);
    },
    mouseup() {
      if (!active || !drawing.current) return;
      drawing.current = false;
      setPts((p) => {
        if (p.length >= 3) onDone(p.map((ll) => [ll.lng, ll.lat]));
        return [];
      });
    },
  });
  useEffect(() => {
    if (active) map.dragging.disable();
    else map.dragging.enable();
    return () => map.dragging.enable();
  }, [active, map]);
  return pts.length > 1 ? <Polyline positions={pts} pathOptions={{ color: '#e8a317', weight: 2, dashArray: '4 4' }} /> : null;
}

export default function WellMapV2({ wells, selected, focusId, onFocus, onSelect, reviews, product = 'gas' }) {
  const [metric, setMetric] = useState('eur');
  const [lasso, setLasso] = useState(false);
  const basemap = useMemo(() => resolveBasemap(), []);
  const located = useMemo(
    () =>
      wells
        .filter((w) => Number.isFinite(w.lat) && Number.isFinite(w.lng))
        .map((w) => ({ ...w, reviewStatus: reviews?.[w.assetId]?.status || 'draft' })),
    [wells, reviews],
  );
  const bounds = useMemo(() => {
    if (!located.length) return null;
    const lats = located.map((w) => w.lat);
    const lngs = located.map((w) => w.lng);
    return [
      [Math.min(...lats), Math.min(...lngs)],
      [Math.max(...lats), Math.max(...lngs)],
    ];
  }, [located]);
  const metrics = useMemo(() => colorMetrics(product), [product]);
  const m = metrics[metric];
  const ramp = useMemo(() => quantileRamp(located.map((w) => m.get(w))), [located, m]);
  const colorOf = (w) => (metric === 'review' ? REVIEW_COLORS[w.reviewStatus] : ramp.color(m.get(w)));
  const fmt = (v) => (v == null ? '—' : v >= 100 ? Math.round(v).toLocaleString() : v.toFixed(1));

  return (
    <div className="aw-card aw-map-card">
      <div className="aw-toolbar">
        <span className="aw-eyebrow">Well map v2</span>
        <label className="aw-inline">
          Colour by{' '}
          <select value={metric} onChange={(e) => setMetric(e.target.value)} data-testid="map-metric">
            {Object.entries(metrics).map(([k, v]) => (
              <option key={k} value={k}>
                {v.label}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          className={`aw-btn ${lasso ? 'aw-btn-amber' : ''}`}
          onClick={() => setLasso((v) => !v)}
          data-testid="lasso-toggle"
          aria-pressed={lasso}
        >
          {lasso ? 'Lasso on — drag to select' : 'Lasso select'}
        </button>
        <button type="button" className="aw-btn" onClick={() => onSelect([])} disabled={!selected.length}>
          Clear ({selected.length})
        </button>
        <span className="aw-muted">
          {located.length}/{wells.length} wells located · joined on API-10 · {basemap.provider}
        </span>
      </div>
      <div className="aw-map-wrap" data-testid="well-map" data-lasso={lasso ? 'on' : 'off'}>
        <MapContainer center={[31.2, -96.5]} zoom={10} style={{ height: 460, width: '100%' }} scrollWheelZoom>
          <TileLayer {...leafletTileLayerProps(basemap)} />
          <Fit bounds={bounds} />
          <Lasso
            active={lasso}
            onDone={(poly) => {
              const hit = lassoSelect(located, poly).map((w) => w.assetId);
              onSelect(hit);
              setLasso(false);
            }}
          />
          {located.map((w) => {
            const sel = selected.includes(w.assetId);
            const focus = focusId === w.assetId;
            return (
              <CircleMarker
                key={w.assetId}
                center={[w.lat, w.lng]}
                radius={focus ? 11 : sel ? 9 : 7}
                className={`aw-well-marker aw-well-${w.assetId}`}
                pathOptions={{
                  color: focus ? '#e8a317' : sel ? '#0c1f2e' : '#ffffff',
                  weight: focus || sel ? 3 : 1.5,
                  fillColor: colorOf(w),
                  fillOpacity: w.history?.length ? 0.92 : 0.45,
                  className: `aw-well-marker aw-well-${w.assetId}`,
                }}
                eventHandlers={{
                  click: (e) => {
                    if (lasso) return;
                    if (e.originalEvent?.shiftKey || e.originalEvent?.metaKey) {
                      onSelect(sel ? selected.filter((id) => id !== w.assetId) : [...selected, w.assetId]);
                    } else onFocus(w.assetId);
                  },
                }}
              >
                <Tooltip>
                  <b>{w.name}</b>
                  <br />
                  {m.label}: {metric === 'review' ? w.reviewStatus : fmt(m.get(w))}
                  <br />
                  {w.status || w.resCat || ''} · API {w.api10 || '—'}
                </Tooltip>
              </CircleMarker>
            );
          })}
        </MapContainer>
      </div>
      <div className="aw-legend">
        {metric === 'review'
          ? Object.entries(REVIEW_COLORS).map(([k, c]) => (
              <span key={k}>
                <i style={{ background: c }} /> {k}
              </span>
            ))
          : ['#dcfce7', '#86efac', '#14b8a6', '#163a4f', '#0c1f2e'].map((c, i) => (
              <span key={c}>
                <i style={{ background: c }} /> {i === 0 ? `≤ ${fmt(ramp.breaks[0])}` : i === 4 ? `> ${fmt(ramp.breaks[3])}` : `≤ ${fmt(ramp.breaks[i])}`}
              </span>
            ))}
        <span>
          <i style={{ background: '#cbd5e1' }} /> no fit / no data
        </span>
        <span className="aw-muted">Click a well for its decline · Shift-click to add · Lasso to select a pool</span>
      </div>
    </div>
  );
}
